import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './Reports.css';

const Reports = () => {
  const { cases, hearings, judgments, judges, advocates } = useApp();
  const [reportType, setReportType] = useState('caseStatus');

  const pendingCases = cases.filter(c => c.status !== 'Disposed');
  const disposedCases = cases.filter(c => c.status === 'Disposed');

  // Judge-wise performance
  const judgePerformance = judges.map(judge => {
    const judgeCases = cases.filter(c => c.judgeName === judge.judgeName);
    const judgeHearings = hearings.filter(h => h.judgeName === judge.judgeName);
    const judgeJudgments = judgments.filter(j => j.caseNumber && cases.find(c => c.caseNumber === j.caseNumber && c.judgeName === judge.judgeName));
    
    return {
      judgeName: judge.judgeName,
      totalCases: judgeCases.length,
      pendingCases: judgeCases.filter(c => c.status !== 'Disposed').length,
      disposedCases: judgeCases.filter(c => c.status === 'Disposed').length,
      totalHearings: judgeHearings.length,
      totalJudgments: judgeJudgments.length,
    };
  });

  // Advocate-wise cases
  const advocateCases = advocates.map(advocate => {
    const advocateMappings = cases.flatMap(c => 
      (c.advocateMappings || []).filter(m => m.advocateName === advocate.advocateName)
    );
    
    return {
      advocateName: advocate.advocateName,
      totalCases: advocateMappings.length,
      activeCases: advocateMappings.filter((_, index) => {
        const caseItem = cases.find(c => 
          (c.advocateMappings || []).some(m => m.advocateName === advocate.advocateName)
        );
        return caseItem && caseItem.status !== 'Disposed';
      }).length,
    };
  });

  // Case type distribution
  const caseTypeDistribution = cases.reduce((acc, caseItem) => {
    acc[caseItem.caseType] = (acc[caseItem.caseType] || 0) + 1;
    return acc;
  }, {});

  const renderCaseStatusReport = () => (
    <div className="report-section">
      <h2>Case Status Report</h2>
      <div className="report-stats">
        <div className="stat-card">
          <div className="stat-value">{cases.length}</div>
          <div className="stat-label">Total Cases</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{pendingCases.length}</div>
          <div className="stat-label">Pending Cases</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{disposedCases.length}</div>
          <div className="stat-label">Disposed Cases</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {cases.length > 0 ? ((disposedCases.length / cases.length) * 100).toFixed(1) : 0}%
          </div>
          <div className="stat-label">Disposal Rate</div>
        </div>
      </div>

      <div className="report-table-card">
        <h3>Case Type Distribution</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Case Type</th>
              <th>Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(caseTypeDistribution).map(([type, count]) => (
              <tr key={type}>
                <td>{type}</td>
                <td>{count}</td>
                <td>{((count / cases.length) * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderJudgePerformanceReport = () => (
    <div className="report-section">
      <h2>Judge-wise Performance Report</h2>
      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Judge Name</th>
              <th>Total Cases</th>
              <th>Pending Cases</th>
              <th>Disposed Cases</th>
              <th>Total Hearings</th>
              <th>Judgments</th>
              <th>Disposal Rate</th>
            </tr>
          </thead>
          <tbody>
            {judgePerformance.map((judge, index) => (
              <tr key={index}>
                <td><strong>{judge.judgeName}</strong></td>
                <td>{judge.totalCases}</td>
                <td>{judge.pendingCases}</td>
                <td>{judge.disposedCases}</td>
                <td>{judge.totalHearings}</td>
                <td>{judge.totalJudgments}</td>
                <td>
                  {judge.totalCases > 0 
                    ? ((judge.disposedCases / judge.totalCases) * 100).toFixed(1) + '%'
                    : '0%'
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAdvocateReport = () => (
    <div className="report-section">
      <h2>Advocate-wise Cases Report</h2>
      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Advocate Name</th>
              <th>Total Cases</th>
              <th>Active Cases</th>
              <th>Completed Cases</th>
            </tr>
          </thead>
          <tbody>
            {advocateCases
              .filter(adv => adv.totalCases > 0)
              .map((advocate, index) => (
                <tr key={index}>
                  <td><strong>{advocate.advocateName}</strong></td>
                  <td>{advocate.totalCases}</td>
                  <td>{advocate.activeCases}</td>
                  <td>{advocate.totalCases - advocate.activeCases}</td>
                </tr>
              ))}
          </tbody>
        </table>
        {advocateCases.filter(adv => adv.totalCases > 0).length === 0 && (
          <p className="empty-state">No advocate case data available.</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>📊 Reports</h1>
      </div>

      <div className="reports-filters">
        <button
          className={reportType === 'caseStatus' ? 'filter-active' : 'filter-btn'}
          onClick={() => setReportType('caseStatus')}
        >
          Case Status Report
        </button>
        <button
          className={reportType === 'judgePerformance' ? 'filter-active' : 'filter-btn'}
          onClick={() => setReportType('judgePerformance')}
        >
          Judge Performance
        </button>
        <button
          className={reportType === 'advocateCases' ? 'filter-active' : 'filter-btn'}
          onClick={() => setReportType('advocateCases')}
        >
          Advocate Cases
        </button>
      </div>

      <div className="reports-content">
        {reportType === 'caseStatus' && renderCaseStatusReport()}
        {reportType === 'judgePerformance' && renderJudgePerformanceReport()}
        {reportType === 'advocateCases' && renderAdvocateReport()}
      </div>
    </div>
  );
};

export default Reports;

